import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { membersService } from "@/services/members.service";

import { MButton } from "@/components/ui/atoms/MButtons";
import FormHeader from "@/components/ui/organisms/MHeader";
import { getMemberInfo } from "@/hooks/useMembers";
import { IconCheck } from "@tabler/icons-react";
import MFrame from "@/components/ui/atoms/MFrame";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  getAddMemberContactConsentScreenName,
} from "@/constants/analytics.constants";
import { useManagedMembersContext } from "@/context/ManagedMembersContext";
import { isChild } from "@/types/member";
import styles from "./AddMemberConsent.module.css";

const CONSENT_PARAGRAPHS = [
  "You have this person’s consent to create and manage their profile.",
  "For a child's profile: you are the parent or legal guardian, or have been authorised by their parent or legal guardian.",
];

const AddMemberConsent: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const memberInfo = getMemberInfo(true);
  const { managedMembers, fetchManagedMembers } = useManagedMembersContext();
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  React.useEffect(() => {
    fetchManagedMembers();
  }, []);

  React.useEffect(() => {
    if (!memberInfo?.id) return;

    const saved = localStorage.getItem("add_member_form");
    let contactType: "phone" | "email" = "phone";
    if (saved) {
      const form = JSON.parse(saved);
      contactType = form.contactOption === "email" ? "email" : "phone";
    }

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
      screenName: getAddMemberContactConsentScreenName(contactType),
    });
  }, [memberInfo?.id]);

  const validate = (form: any) => {
    const errs: Record<string, string> = {};
    if (!form.firstName) errs.firstName = "First name is required";
    if (!form.lastName) errs.lastName = "Last name is required";
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
      errs.email = "Invalid email";
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const saved = localStorage.getItem("add_member_form");
    const form = saved ? JSON.parse(saved) : {};

    const genderMap: Record<string, number> = { male: 1, female: 2, other: 3 };
    if (typeof form.gender === "string") {
      form.gender = genderMap[form.gender.toLowerCase()] ?? null;
    }

    const resolvedFamilyId =
      memberInfo.current_family || memberInfo.familyId || null;
    form.family_id = resolvedFamilyId;
    form.current_family = resolvedFamilyId;

    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      setIsSubmitting(false);
      return;
    }

    const isFirstFamilyMember = managedMembers.length === 1;

    const isChildMember =
      form.dobYear &&
      form.dobMonth &&
      isChild({
        dob_year: Number(form.dobYear),
        dob_month: Number(form.dobMonth),
        dob_day: 1,
      });

    try {
      await membersService.createMember(form);
      localStorage.removeItem("add_member_form");
      navigate("/profile/family/add-member/success", {
        state: {
          memberName: form.firstName || form.first_name,
          invite: form.invite,
          nickname: form.nickname,
          isFirstFamilyMember,
          isChildOrPet: isChildMember,
        },
      });
    } catch (err: any) {
      let errorMsg = "Failed to add member.";
      if (err?.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err?.message) {
        errorMsg = err.message;
      }
      setError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="profile-page-root flex flex-col"
      style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 128px)" }}
    >
      <FormHeader
        module="Family"
        memberInfo={memberInfo}
        title="Before we complete"
        description="We need your consent to add this member to your family."
        hideLearnMore
      />
      <div className="flex-1">
        <div className={styles.consentContainer}>
          <MFrame className={styles.consentCard}>
            <div className={styles.consentHeading}>You confirm that:</div>

            {CONSENT_PARAGRAPHS.map((text, idx) => (
              <div
                key={idx}
                className={styles.consentItem}
                style={idx === 1 ? { alignItems: "center" } : {}}
              >
                <IconCheck
                  size={24}
                  className={styles.consentCheckIcon}
                  style={idx === 1 ? { marginTop: "0" } : {}}
                />
                <span className={styles.consentText}>{text}</span>
              </div>
            ))}
          </MFrame>
        </div>

        <div className={styles.privacyLinkContainer}>
          <span className={styles.privacyText}>View our complete </span>
          <button
            type="button"
            onClick={() =>
              window.open(
                "https://maatra.health/privacy",
                "_blank",
                "noopener noreferrer"
              )
            }
            className={styles.privacyLink}
          >
            Privacy policy
          </button>
          <span className={styles.privacyText}>.</span>
        </div>

        {errors && Object.keys(errors).length > 0 && (
          <div className={styles.errorContainer}>
            <div className={styles.errorList}>
              {Object.values(errors).map((err, idx) => (
                <div key={idx}>{err}</div>
              ))}
            </div>
          </div>
        )}
        {error && (
          <div className={styles.errorContainer}>
            <div className={styles.errorText}>{error}</div>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="confirm-footer">
        <MButton
          type="submit"
          variant="filled"
          size="lg"
          className="w-full"
          disabled={isSubmitting}
        >
          Done
        </MButton>
      </form>
    </div>
  );
};

export default AddMemberConsent;
